import { useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useAuthService } from '../services/authService';
import { 
  getCustomerByEmail, 
  getSubscriptions, 
  cancelSubscription,
  updateUserUnsubscribed 
} from '../services/stripeService';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  Snackbar
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';

function Billing() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const { user, isAuthenticated } = useAuthService();

  const fetchSubscriptionData = async () => {
    if (!user || !user.attributes?.email) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get Stripe customer
      const customer = await getCustomerByEmail(user.attributes.email);
      
      if (customer) {
        // Get subscriptions
        const subscriptionsResponse = await getSubscriptions(customer.id);
        const activeSubscription = subscriptionsResponse.data?.find(sub => 
          sub.status === 'active' || sub.status === 'trialing'
        );
        
        setSubscription(activeSubscription || null);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setError('Failed to load subscription information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptionData();
    }
  }, [isAuthenticated, user]);

  const handleCancelSubscription = async () => {
    if (!subscription) return;
    
    try {
      setCancelLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      // Cancel the subscription
      const result = await cancelSubscription(subscription.id);
      
      if (result.cancel_at_period_end || result.status === 'canceled') {
        // Clear subscription to show "no active subscription" UI immediately
        setSubscription(null);
        
        // Close dialog
        setOpenDialog(false);
        
        // Show success message for immediate cancellation
        setSuccessMessage('Your subscription has been canceled successfully.');
        
        console.log('Subscription has been canceled');
        
        // Convert the Unix timestamp to ISO string for DynamoDB
        const subscriptionEndDate = new Date().toISOString(); // Use current time since subscription ends immediately
        
        // Update DynamoDB user table to mark subscription as inactive
        if (user?.userId) {
          try {
            await updateUserUnsubscribed(user.userId, subscriptionEndDate);
            console.log('User subscription status updated in DynamoDB');
          } catch (dbError) {
            console.error('Error updating user subscription status in DynamoDB:', dbError);
            // Don't show error to user as the Stripe cancellation was successful
            // Log the event for monitoring
            console.warn('DynamoDB update failed, but Stripe cancellation was successful. User data might be out of sync.');
          }
        }
      } else {
        // Something went wrong
        throw new Error('Failed to cancel subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setError(error.message || 'Failed to cancel subscription. Please try again.');
      
      // Close the dialog if there was an error
      setOpenDialog(false);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccessMessage(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'trialing': return 'info';
      case 'canceled': return 'error';
      default: return 'default';
    }
  };

  return (
    <HelmetProvider>
      <>
        <Helmet>
          <title>Manage Billing | Screamify™</title>
          <meta name="description" content="Manage your Screamify subscription and billing details." />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Box 
          component="main"
          role="main"
          sx={{ 
            bgcolor: '#000000',
            minHeight: '100vh',
            width: '100vw',
            position: 'fixed',
            top: 0,
            left: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
          }}
        >
          <Header />
          <Container maxWidth="md" sx={{ 
            flex: 1, 
            py: 4,
            mt: { xs: 6, sm: 7 },
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography
              variant="h1"
              align="center"
              sx={{
                color: '#ff0000',
                fontWeight: 500,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.7rem' },
                mb: 4,
                pt: 2,
                textShadow: '0 0 10px rgba(255, 0, 0, 0.3)'
              }}
            >
              Billing & Subscription
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                <CircularProgress sx={{ color: '#ff0000' }} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 4 }}>
                {error}
              </Alert>
            ) : (
              <Paper
                elevation={4}
                sx={{
                  bgcolor: 'rgba(20, 20, 20, 0.9)',
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                {subscription ? (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography 
                        variant="h5" 
                        sx={{ color: '#ffffff', mb: 1 }}
                      >
                        Your Subscription
                      </Typography>
                      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 2 }} />
                      
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                          <Card sx={{ bgcolor: 'rgba(30, 30, 30, 0.7)', height: '100%' }}>
                            <CardContent>
                              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                Status
                              </Typography>
                              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                <Chip 
                                  label={subscription.status.toUpperCase()} 
                                  color={getStatusColor(subscription.status)}
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                                <Typography sx={{ color: '#ffffff' }}>
                                  {subscription.cancel_at_period_end ? '(Cancels at period end)' : ''}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card sx={{ bgcolor: 'rgba(30, 30, 30, 0.7)', height: '100%' }}>
                            <CardContent>
                              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                Price
                              </Typography>
                              <Typography sx={{ color: '#ffffff', fontWeight: 500, mt: 1 }}>
                                ${(subscription.plan?.amount / 100).toFixed(2)} / {subscription.plan?.interval}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card sx={{ bgcolor: 'rgba(30, 30, 30, 0.7)', height: '100%' }}>
                            <CardContent>
                              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                Current Period Start
                              </Typography>
                              <Typography sx={{ color: '#ffffff', mt: 1 }}>
                                {formatDate(subscription.current_period_start)}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card sx={{ bgcolor: 'rgba(30, 30, 30, 0.7)', height: '100%' }}>
                            <CardContent>
                              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                Current Period End
                              </Typography>
                              <Typography sx={{ color: '#ffffff', mt: 1 }}>
                                {formatDate(subscription.current_period_end)}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ textAlign: 'center', mt: 5, mb: 2 }}>
                      {subscription.cancel_at_period_end ? (
                        <Typography 
                          sx={{ 
                            color: '#ffffff', 
                            fontSize: '1.1rem',
                            fontWeight: 500,
                            opacity: 0.9
                          }}
                        >
                          Subscription Canceled
                        </Typography>
                      ) : (
                        <Button
                          variant="outlined"
                          onClick={() => setOpenDialog(true)}
                          size="large"
                          sx={{
                            borderColor: '#ff0000',
                            color: '#ff0000',
                            borderWidth: 2,
                            px: 4,
                            py: 1,
                            '&:hover': {
                              borderColor: '#cc0000',
                              backgroundColor: 'rgba(255, 0, 0, 0.04)',
                              borderWidth: 2
                            }
                          }}
                        >
                          Cancel Subscription
                        </Button>
                      )}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography sx={{ color: '#ffffff', mb: 3 }}>
                      You don't have an active subscription.
                    </Typography>
                    <Button
                      variant="contained"
                      href="/subscribe"
                      sx={{
                        bgcolor: '#ff0000',
                        '&:hover': {
                          bgcolor: '#cc0000'
                        }
                      }}
                    >
                      Subscribe Now
                    </Button>
                  </Box>
                )}
              </Paper>
            )}
          </Container>
          <Footer />
        </Box>

        {/* Confirmation Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => !cancelLoading && setOpenDialog(false)}
        >
          <DialogTitle sx={{ bgcolor: '#111', color: '#fff' }}>
            Cancel Subscription
          </DialogTitle>
          <DialogContent sx={{ bgcolor: '#111', color: '#fff' }}>
            <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period on {formatDate(subscription?.current_period_end)}.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ bgcolor: '#111', px: 3, pb: 3 }}>
            <Button 
              onClick={() => setOpenDialog(false)} 
              disabled={cancelLoading}
              sx={{ color: '#fff' }}
            >
              Keep Subscription
            </Button>
            <Button 
              onClick={handleCancelSubscription} 
              color="error" 
              disabled={cancelLoading}
              sx={{ 
                bgcolor: 'rgba(255,0,0,0.1)', 
                '&:hover': { bgcolor: 'rgba(255,0,0,0.2)' } 
              }}
            >
              {cancelLoading ? <CircularProgress size={24} color="error" /> : 'Confirm Cancellation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add the success snackbar */}
        <Snackbar 
          open={!!successMessage} 
          autoHideDuration={6000} 
          onClose={handleCloseSuccess}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </>
    </HelmetProvider>
  );
}

export default Billing; 